import React, { useEffect, useState } from 'react'
import { collection, query, where, orderBy, onSnapshot, addDoc, updateDoc, doc } from 'firebase/firestore'
import { db } from '../../firebase.config'
import { useAuth } from '../../context/AuthContext'
import AppLayout from '../../components/layout/AppLayout'

const TYPE_COLORS: Record<string, string> = { 'Safety Alert': '#dc2626', 'Event': '#f59e0b', 'General': '#2563eb', 'Tasking': '#7c3aed' }
const TYPE_BG: Record<string, string> = { 'Safety Alert': '#fef2f2', 'Event': '#fffbeb', 'General': '#eff6ff', 'Tasking': '#f5f3ff' }

const TASK_STATUS_COLORS: Record<string, string> = { pending: '#f59e0b', acknowledged: '#2563eb', completed: '#16a34a' }

export default function CommunicationsPage() {
  const { orgId, userData, isAdmin, isSupervisor } = useAuth()
  const [messages, setMessages] = useState<any[]>([])
  const [users, setUsers] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [showCompose, setShowCompose] = useState(false)
  const [compose, setCompose] = useState({ type: 'General', title: '', body: '', recipients: 'all' as string | string[], isTask: false })
  const [selectedUsers, setSelectedUsers] = useState<string[]>([])

  // Load org users for targeting
  useEffect(() => {
    if (!orgId) return
    const unsub = onSnapshot(
      query(collection(db, 'organizations', orgId, 'users')),
      snap => { setUsers(snap.docs.map(d => ({ id: d.id, ...d.data() }))) }
    )
    return () => unsub()
  }, [orgId])

  // Load messages
  useEffect(() => {
    if (!orgId) return
    const unsub = onSnapshot(
      query(collection(db, 'organizations', orgId, 'communications'), orderBy('createdAt', 'desc')),
      snap => { setMessages(snap.docs.map(d => ({ id: d.id, ...d.data() }))); setLoading(false) }
    )
    return () => unsub()
  }, [orgId])

  const handleSend = async () => {
    if (!compose.title.trim() || !compose.body.trim()) return
    if (compose.isTask && compose.recipients === 'specific' && selectedUsers.length === 0) {
      alert('Please select at least one recipient for the task.')
      return
    }

    const recipients = compose.recipients === 'all' ? ['all'] : selectedUsers

    await addDoc(collection(db, 'organizations', orgId, 'communications'), {
      orgId,
      type: compose.type,
      title: compose.title.trim(),
      body: compose.body.trim(),
      senderId: userData?.uid,
      senderName: userData?.name || 'Admin',
      readBy: [userData?.uid],
      isTask: compose.isTask,
      recipients,
      taskStatus: recipients.map((rid: string) => ({
        userId: rid === 'all' ? 'all' : rid,
        status: rid === 'all' ? 'completed' : 'pending',
        acknowledgedAt: null,
        completedAt: null
      })),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    })

    setShowCompose(false)
    setCompose({ type: 'General', title: '', body: '', recipients: 'all', isTask: false })
    setSelectedUsers([])
  }

  const handleAcknowledge = async (msgId: string) => {
    await updateDoc(doc(db, 'organizations', orgId, 'communications', msgId), {
      taskStatus: messages.find(m => m.id === msgId)?.taskStatus.map((t: any) =>
        t.userId === userData?.uid || t.userId === 'all'
          ? { ...t, status: 'acknowledged', acknowledgedAt: new Date().toISOString() }
          : t
      ),
      updatedAt: new Date().toISOString()
    })
  }

  const handleComplete = async (msgId: string) => {
    await updateDoc(doc(db, 'organizations', orgId, 'communications', msgId), {
      taskStatus: messages.find(m => m.id === msgId)?.taskStatus.map((t: any) =>
        t.userId === userData?.uid || t.userId === 'all'
          ? { ...t, status: 'completed', completedAt: new Date().toISOString() }
          : t
      ),
      updatedAt: new Date().toISOString()
    })
  }

  const isRecipient = (msg: any) => {
    if (!userData) return false
    const ts = msg.taskStatus || []
    return ts.some((t: any) => t.userId === userData.uid || t.userId === 'all')
  }

  const getMyTaskStatus = (msg: any) => {
    const ts = msg.taskStatus || []
    return ts.find((t: any) => t.userId === userData?.uid || t.userId === 'all')?.status || 'pending'
  }

  const formatDate = (iso: string) => iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''

  const toggleUser = (uid: string) => {
    setSelectedUsers(prev => prev.includes(uid) ? prev.filter(u => u !== uid) : [...prev, uid])
  }

  return (
    <AppLayout title="Communications">
      <div className="mb-6 flex items-center justify-between">
        <input type="text" placeholder="Search messages..." className="border border-gray-300 rounded-lg px-4 py-2 text-sm w-64" />
        {isSupervisor() && (
          <button
            onClick={() => setShowCompose(!showCompose)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700"
          >
            {showCompose ? '✕ Cancel' : '+ Compose'}
          </button>
        )}
      </div>

      {/* Compose Panel */}
      {showCompose && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-6">
          <h3 className="font-semibold text-gray-900 mb-4">New Message</h3>

          {/* Message Type */}
          <div className="mb-4 flex gap-4">
            {['Safety Alert', 'Event', 'General', 'Tasking'].map(t => (
              <label key={t} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="type"
                  value={t}
                  checked={compose.type === t}
                  onChange={() => setCompose(c => ({ ...c, type: t, isTask: t === 'Tasking' }))}
                />
                <span className="text-sm font-medium" style={{ color: TYPE_COLORS[t] }}>{t}</span>
              </label>
            ))}
          </div>

          {/* Targeting — only for Tasking */}
          {compose.isTask && (
            <div className="mb-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Send To</p>
              <div className="flex gap-4 mb-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="recipients"
                    value="all"
                    checked={compose.recipients === 'all'}
                    onChange={() => setCompose(c => ({ ...c, recipients: 'all' }))}
                  />
                  <span className="text-sm text-gray-700">Everyone</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="recipients"
                    value="specific"
                    checked={compose.recipients === 'specific'}
                    onChange={() => setCompose(c => ({ ...c, recipients: 'specific' }))}
                  />
                  <span className="text-sm text-gray-700">Specific Users</span>
                </label>
              </div>

              {compose.recipients === 'specific' && (
                <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto border border-gray-200 rounded-lg p-3 bg-gray-50">
                  {users.map(u => (
                    <button
                      key={u.id}
                      onClick={() => toggleUser(u.id)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                        selectedUsers.includes(u.id)
                          ? 'bg-purple-100 border-purple-400 text-purple-700'
                          : 'bg-white border-gray-300 text-gray-600 hover:border-purple-300'
                      }`}
                    >
                      {u.name || u.email} {u.role ? `(${u.role})` : ''}
                    </button>
                  ))}
                  {selectedUsers.length > 0 && (
                    <span className="text-xs text-gray-500 self-center ml-2">{selectedUsers.length} selected</span>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Title */}
          <input
            className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm mb-3"
            placeholder="Title"
            value={compose.title}
            onChange={e => setCompose(c => ({ ...c, title: e.target.value }))}
          />

          {/* Body */}
          <textarea
            className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm mb-3 min-h-24"
            placeholder={compose.isTask ? "Describe the task, expected completion time, and any instructions..." : "Message body"}
            value={compose.body}
            onChange={e => setCompose(c => ({ ...c, body: e.target.value }))}
          />

          <div className="flex gap-3">
            <button
              onClick={handleSend}
              className="bg-blue-600 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-blue-700"
            >
              {compose.isTask ? 'Send Task' : 'Send Message'}
            </button>
            <button onClick={() => { setShowCompose(false); setSelectedUsers([]); setCompose({ type: 'General', title: '', body: '', recipients: 'all', isTask: false }) }} className="text-gray-500 px-5 py-2 text-sm hover:text-gray-700">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Message List */}
      <div className="space-y-3">
        {messages.map(m => {
          const myStatus = m.isTask ? getMyTaskStatus(m) : null
          const isMyTask = m.isTask && isRecipient(m)
          const completedCount = m.taskStatus?.filter((t: any) => t.status === 'completed').length || 0
          const totalRecipients = m.taskStatus?.filter((t: any) => t.userId !== 'all').length || 0

          return (
            <div key={m.id} className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <span
                    className="text-xs px-2 py-0.5 rounded font-medium"
                    style={{ backgroundColor: TYPE_BG[m.type] || '#f5f5f5', color: TYPE_COLORS[m.type] || '#888' }}
                  >
                    {m.type || 'General'}
                  </span>
                  {m.isTask && (
                    <span className="text-xs px-2 py-0.5 rounded font-medium bg-purple-100 text-purple-700">Task</span>
                  )}
                  <h3 className="font-semibold text-gray-900 text-sm mt-0.5">{m.title}</h3>
                </div>
                <span className="text-xs text-gray-400 whitespace-nowrap ml-2">{formatDate(m.createdAt)}</span>
              </div>
              <p className="text-sm text-gray-600 mb-3">{m.body}</p>

              {/* Task status bar */}
              {m.isTask && (
                <div className="flex items-center gap-3 mb-3">
                  {/* Per-recipient task status (admin/supervisor view) */}
                  {isAdmin() || isSupervisor() ? (
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs text-gray-500">
                        {completedCount}/{totalRecipients} completed
                      </span>
                      <div className="flex gap-1">
                        {m.taskStatus?.filter((t: any) => t.userId !== 'all').map((t: any) => {
                          const user = users.find(u => u.id === t.userId)
                          return (
                            <span
                              key={t.userId}
                              className="text-xs px-2 py-0.5 rounded font-medium"
                              style={{ backgroundColor: TASK_STATUS_COLORS[t.status] + '20', color: TASK_STATUS_COLORS[t.status] }}
                              title={user?.name || t.userId}
                            >
                              {user?.name?.[0] || '?'}
                            </span>
                          )
                        })}
                      </div>
                    </div>
                  ) : null}

                  {/* Assignee action buttons */}
                  {isMyTask && (
                    <div className="flex gap-2 ml-auto">
                      {myStatus === 'pending' && (
                        <button
                          onClick={() => handleAcknowledge(m.id)}
                          className="px-3 py-1.5 rounded-lg text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100"
                        >
                          Acknowledge
                        </button>
                      )}
                      {myStatus === 'acknowledged' && (
                        <button
                          onClick={() => handleComplete(m.id)}
                          className="px-3 py-1.5 rounded-lg text-xs font-medium bg-green-50 text-green-700 border border-green-200 hover:bg-green-100"
                        >
                          Mark Complete
                        </button>
                      )}
                      {myStatus === 'completed' && (
                        <span className="flex items-center gap-1 text-xs text-green-600 font-medium">
                          ✓ Completed
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}

              <p className="text-xs text-gray-400">
                From: {m.senderName || 'Unknown'}
                {m.isTask && (isAdmin() || isSupervisor()) && ` • ${totalRecipients} recipients`}
              </p>
            </div>
          )
        })}
        {messages.length === 0 && !loading && (
          <p className="text-center py-12 text-gray-400">No messages found</p>
        )}
      </div>
    </AppLayout>
  )
}